/**
 * Versioned asset URLs, from the asset manifest (#285). Each thread that
 * loads assets (the page, and every render or game worker) loads the
 * manifest once; after that `versionedAssetUrl` turns an asset path into
 * its `?v=` URL, which the server serves immutable, so a cached copy is used
 * with no request at all. Until the manifest has loaded (or for a file it
 * doesn't list) the plain URL is returned, which still works: it is only
 * revalidated on use. The manifest also hands the model cache every model's
 * content hash, so a cached model is used without asking.
 */
import { Tools } from '@babylonjs/core';
import { createLogger, toAssetVersion, withAssetVersion, type AssetManifest, type AssetManifestEntry } from '@sorrem/shared';
import { registerModelContentHashes } from './model-cache';

const logger = createLogger('engine:asset-versions');

const MANIFEST_URL = '/assets/manifest';
const ASSETS_PREFIX = '/assets/';

let manifest: AssetManifest | null = null;
let entriesByPath: ReadonlyMap<string, AssetManifestEntry> = new Map();
let loading: Promise<AssetManifest | null> | null = null;
const listeners = new Set<() => void>();

/** An asset path as the manifest keys it: under `/assets/`, without its query. */
function resolveManifestPath(path: string): string {
    const withoutQuery = path.split('?')[0] ?? path;
    const withoutOrigin = withoutQuery.replace(/^[a-z]+:\/\/[^/]+/i, '');
    return withoutOrigin.startsWith(ASSETS_PREFIX) ? withoutOrigin.slice(ASSETS_PREFIX.length) : withoutOrigin.replace(/^\//, '');
}

/** Takes a manifest in (the loaded one, or one a test hands over): its versions, and its models' hashes for the model cache. */
export function adoptAssetManifest(loaded: AssetManifest): void {
    manifest = loaded;
    entriesByPath = new Map(loaded.entries.map((entry) => [entry.path, entry]));
    for (const entry of loaded.entries) {
        for (const model of entry.models ?? []) {
            registerModelContentHashes(model.kind, [[model.id, entry.hash]]);
        }
    }
    for (const listener of listeners) {
        listener();
    }
}

/** Hears each manifest this thread takes in (a page's remembered copy, then the server's). Returns the unsubscribe. */
export function onAssetManifestChange(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/**
 * Loads this thread's asset manifest, once; later calls share the first.
 * Resolves null when it can't load: every URL then stays plain.
 */
export function loadAssetManifest(): Promise<AssetManifest | null> {
    loading ??= fetch(MANIFEST_URL, { headers: { Accept: 'application/json' } })
        .then(async (response) => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }
            const loaded = await response.json() as AssetManifest;
            adoptAssetManifest(loaded);
            return loaded;
        })
        .catch((error: unknown) => {
            logger.warn('asset manifest did not load; asset URLs stay unversioned:', error);
            return null;
        });
    return loading;
}

/** The manifest this thread has loaded, or null before it has. */
export function getAssetManifest(): AssetManifest | null {
    return manifest;
}

/** A file's manifest entry, by its path (`/assets/...`, or relative to it). */
export function findAssetEntry(path: string): AssetManifestEntry | undefined {
    return entriesByPath.get(resolveManifestPath(path));
}

/** Whether the manifest lists a file. */
export function hasAsset(path: string): boolean {
    return entriesByPath.has(resolveManifestPath(path));
}

/**
 * An asset's URL with its version, for one under `/assets/` the manifest
 * lists: `url` itself otherwise (or before the manifest has loaded).
 * Accepts `/assets/...`, a path relative to it, or a full URL.
 */
export function versionedAssetUrl(url: string): string {
    // In-memory sources (models handed over as data URIs, which can be megabytes) never are.
    if (url.startsWith('data:') || url.startsWith('blob:')) {
        return url;
    }
    const entry = entriesByPath.get(resolveManifestPath(url));
    if (entry === undefined) {
        return url;
    }
    const absolute = url.startsWith('/') || /^[a-z]+:\/\//i.test(url) ? url : `${ASSETS_PREFIX}${url}`;
    return withAssetVersion(absolute, toAssetVersion(entry.hash));
}

/**
 * Has every URL Babylon loads in this thread (textures, sprites, models
 * loaded by URL) go through `versionedAssetUrl`, and starts loading the
 * manifest it reads. Call once per thread, before building a scene; await
 * the result to have the manifest in hand first.
 */
export function installVersionedAssetUrls(): Promise<AssetManifest | null> {
    Tools.PreprocessUrl = versionedAssetUrl;
    return loadAssetManifest();
}
